import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Dialog } from "@mui/material";
import { type Theme, ThemeProvider } from "@mui/material/styles";
import {
  type Dispatch,
  type FormEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import GfgButton from "@/components/ui/GfgButton";
import BuyCreditsModal from "@/components/usersOrgs/BuyCreditsModal";
import { COPY, DEFAULT_RECIPIENT_COUNTRY } from "@/constants/campaignContacts";
import { CAMPAIGN_STATUSES } from "@/constants/campaings";
import { useCampaignContactActions, useRecipientAdditionsQuote } from "@/hooks/useCampaignContacts";
import type {
  AddCampaignRecipientsBody,
  AddRecipientsResult,
  CampaignContactsResponse,
  CampaignContactWire,
  DepositPreference,
  RecipientAdditionRowBody,
  RecipientAdditionsQuote,
} from "@/interfaces/campaignContacts";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError-helper";
import { formatUsd } from "@/utils/number-format";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";
import AddRecipientPaymentStep from "./AddRecipientPaymentStep";
import AddRecipientSendTimeStep from "./AddRecipientSendTimeStep";
import {
  additionBody,
  confirmFieldErrors,
  defaultSender,
  defaultSendTime,
  detailsCostLine,
  hasSendTimeStep,
  initialWizardState,
  isLastStep,
  MONEY_REFUSAL_STATUS,
  needsPaymentReview,
  paymentRecap,
  primaryActionLabel,
  primaryDisabled,
  recipientFormErrors,
  samePlan,
  sendStepError,
  wizardPlanOf,
  wizardReducer,
  wizardSteps,
  wizardTitle,
} from "./addRecipients-helper";
import { CONTACTS_FORM_PAPER_SX, ContactsDialogHeader } from "./ContactsDialog";
import { maxSendDateInput, minSendDateInput } from "./campaignContacts-helper";
import ImportReviewNotices from "./ImportReviewNotices";
import RecipientDetailsForm from "./RecipientDetailsForm";
import StepNotice from "./StepNotice";
import type {
  BulkImportReview,
  RecipientFormErrors,
  RecipientFormValue,
  SendTimeCampaign,
  WizardAction,
  WizardEntry,
  WizardPlan,
  WizardState,
} from "./types";
import { useAdditionCreditsPurchase } from "./useAdditionCreditsPurchase";

interface AddRecipientWizardProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  envelope: CampaignContactsResponse;
  entry: WizardEntry;
  /** Bulk: the imported rows, already deduped against the campaign. Unused for a single add. */
  initialRows?: readonly RecipientAdditionRowBody[];
  /** Bulk only, from the importer: the skipped line and the errors that block the add. */
  importReview?: Pick<BulkImportReview, "skipped" | "errors">;
  /** After the write (the only one, B8), with the toast to show; the wizard then closes itself. */
  onAdded: (result: AddRecipientsResult, toast: string) => void;
}

type SubmitAddition = (body: AddCampaignRecipientsBody) => Promise<AddRecipientsResult>;

const NO_ROWS: readonly RecipientAdditionRowBody[] = [];
/** `failed` needs a status; a request that never reached the server carries none. */
const NO_STATUS = 0;

// ---- pure pieces ---------------------------------------------------------------------------------

function sendTimeCampaignOf(envelope: CampaignContactsResponse): SendTimeCampaign {
  const c = envelope.campaign;
  return {
    status: c.status,
    automation: envelope.automation,
    manual: c.manual,
    scheduledDate: c.scheduledDate,
    expireDate: c.expireDate,
    scheduledTz: c.scheduledTz,
  };
}

/** A blank recipient signed by the campaign's first sender, as the server's `firstContactSender`. */
function blankRecipient(contacts: readonly CampaignContactWire[]): RecipientFormValue {
  const sender = defaultSender(contacts);
  return {
    firstName: "",
    lastName: "",
    email: "",
    country: DEFAULT_RECIPIENT_COUNTRY,
    senderFirstName: sender.firstName,
    senderLastName: sender.lastName,
    senderCompany: sender.company,
    sendDate: "",
  };
}

/** The server's refusal when it sent one; a request that never got an answer gets the retry line. */
function quoteFailure(error: unknown): string {
  const fallback = COPY.addWizard.quoteError;
  return apiErrorStatus(error) === undefined ? fallback : apiErrorMessage(error, fallback);
}

function addedToast(result: AddRecipientsResult, campaignName: string): string {
  const budget = result.approvedBudgetAfter === null ? null : formatUsd(result.approvedBudgetAfter);
  return COPY.addWizard.toastAdded(result.contactIds.length, campaignName, budget);
}

/**
 * JR's Buy Credits drawer opens over this dialog, but MUI layers a Drawer (`zIndex.drawer`, 1200)
 * under a Dialog (`zIndex.modal`, 1300). Inside the drawer's subtree both layers move above the
 * dialog and keep their order, so whatever the drawer opens itself still stacks over it.
 */
export function aboveWizardDialog(outer: Theme): Theme {
  const { modal } = outer.zIndex;
  return { ...outer, zIndex: { ...outer.zIndex, drawer: modal + 1, modal: modal + 2 } };
}

// ---- hooks ---------------------------------------------------------------------------------------

/** Keeps the steps in line with the latest quote (never while a confirm is in flight). */
function useReplan(state: WizardState, plan: WizardPlan, dispatch: Dispatch<WizardAction>): void {
  useEffect(() => {
    if (state.busy || samePlan(state.plan, plan)) return;
    dispatch({ type: "replan", plan });
    if (state.plan.paid || !plan.paid || state.step !== "sendTime") return;
    // Now a paid addition: the write must follow figures the user has seen, so back to payment
    // with the send time kept (R16), carrying the refusal that caused the re-quote, if any. It is
    // replayed with the money-refusal status, the reducer's one way back to the figures with a notice.
    dispatch(
      state.notice === null
        ? { type: "change" }
        : { type: "failed", status: MONEY_REFUSAL_STATUS, message: state.notice },
    );
  }, [state.busy, state.plan, state.step, state.notice, plan, dispatch]);
}

/** On send time, a re-quote that is short of credits again goes back to the payment step. */
function usePaymentReview(
  state: WizardState,
  quote: RecipientAdditionsQuote | undefined,
  dispatch: Dispatch<WizardAction>,
): void {
  const review = !state.busy && quote !== undefined && needsPaymentReview(state, quote);
  useEffect(() => {
    if (review) dispatch({ type: "change" });
  }, [review, dispatch]);
}

/**
 * The wizard's state and the quote it follows. The reducer comes first, planned from the envelope's
 * free spaces, because the quote is asked for its postpaid preference; the quote then re-plans it.
 */
function useWizardState(i: {
  campaignId: string;
  envelope: CampaignContactsResponse;
  campaign: SendTimeCampaign;
  entry: WizardEntry;
  rows: readonly RecipientAdditionRowBody[];
  n: number;
}) {
  const { entry, n, campaign } = i;
  const draft = i.envelope.campaign.status === CAMPAIGN_STATUSES.DRAFT;
  const spacesFree = i.envelope.spacesFree;
  const sendStep = hasSendTimeStep(campaign, entry, i.rows);
  const [state, dispatch] = useReducer(wizardReducer, null, () =>
    initialWizardState(
      wizardPlanOf({ entry, draft, n, spacesFree, quote: undefined, sendStep }),
      defaultSendTime(campaign, new Date()),
      minSendDateInput(resolveScheduleTimeZone(campaign.scheduledTz)),
    ),
  );
  // A draft has no budget to price against: it adds rows only (B14).
  const quoteBody = { count: n, depositMode: state.depositPreference };
  const quoteQuery = useRecipientAdditionsQuote(i.campaignId, quoteBody, !draft);
  const quote = quoteQuery.data;
  const plan = useMemo(
    () => wizardPlanOf({ entry, draft, n, spacesFree, quote, sendStep }),
    [entry, draft, n, spacesFree, quote, sendStep],
  );
  useReplan(state, plan, dispatch);
  usePaymentReview(state, quote, dispatch);
  // Another deposit mode's quote keeps the previous figures up while it loads (F6): they are stale,
  // so nothing moves on from them until the fresh quote is in.
  const quoteStale = quoteQuery.isFetching || quoteQuery.isPlaceholderData;
  return { state, dispatch, quoteQuery, quote, quoteStale };
}

/**
 * The recipient form: its value, its field errors and the check run before leaving details. Back on
 * details with a server notice (a 409), it re-checks against the refetched envelope, so an email
 * added meanwhile shows inline on Email (spec error table), not only in the notice. A refused
 * confirm's own field errors (`confirmFieldErrors`, F1) show beside them until the next edit.
 */
function useRecipientForm(i: {
  envelope: CampaignContactsResponse;
  campaign: SendTimeCampaign;
  recheck: boolean;
}) {
  const { recheck } = i;
  const { automation, expireDate, scheduledTz } = i.campaign;
  const [form, setFormValue] = useState(() => blankRecipient(i.envelope.contacts));
  const [checked, setChecked] = useState<RecipientFormErrors>({});
  const [refused, setRefused] = useState<RecipientFormErrors>({});
  const contacts = i.envelope.contacts;
  const existingEmails = useMemo(() => contacts.map((c) => c.email), [contacts]);
  const validate = useCallback((): boolean => {
    const timeZone = resolveScheduleTimeZone(scheduledTz);
    const ctx = { automation, existingEmails, now: new Date(), expireDate, timeZone };
    const next = recipientFormErrors(form, ctx);
    setChecked(next);
    return Object.keys(next).length === 0;
  }, [form, automation, existingEmails, expireDate, scheduledTz]);
  useEffect(() => {
    if (recheck) validate();
  }, [recheck, validate]);
  function setForm(value: RecipientFormValue) {
    setFormValue(value);
    setRefused({});
  }
  const errors: RecipientFormErrors = { ...checked, ...refused };
  return { form, setForm, errors, validate, showFieldErrors: setRefused };
}

/**
 * The write. A refusal goes back into the wizard with its HTTP status (B9): the reducer decides the
 * step and keeps any purchase made, and `confirmFieldErrors` picks the field it names (F1). Success
 * hands the result up and closes.
 */
function useConfirm(i: {
  submit: SubmitAddition;
  dispatch: Dispatch<WizardAction>;
  onFieldErrors: (errors: RecipientFormErrors) => void;
  campaignName: string;
  onAdded: AddRecipientWizardProps["onAdded"];
  onClose: () => void;
}) {
  const inFlight = useRef(false);
  return async function confirm(body: AddCampaignRecipientsBody): Promise<void> {
    if (inFlight.current) return;
    inFlight.current = true;
    i.dispatch({ type: "submit" });
    let result: AddRecipientsResult;
    try {
      result = await i.submit(body);
    } catch (err) {
      const status = apiErrorStatus(err) ?? NO_STATUS;
      const message = apiErrorMessage(err);
      i.dispatch({ type: "failed", status, message });
      i.onFieldErrors(confirmFieldErrors(status, message));
      return;
    } finally {
      inFlight.current = false;
    }
    i.onAdded(result, addedToast(result, i.campaignName));
    i.onClose();
  };
}

/**
 * The payment step's credits controls (R4): the postpaid choice, and JR's Buy Credits modal for
 * the credits the wallet lacks. The modal's props come back apart; the wizard renders it.
 */
function useCreditsPurchase(i: {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
  quote: RecipientAdditionsQuote | undefined;
  campaign: SendTimeCampaign;
  refetchQuote: () => void;
}) {
  const { state, dispatch } = i;
  const purchase = useAdditionCreditsPurchase({
    quote: i.quote,
    state,
    dispatch,
    timeZone: resolveScheduleTimeZone(i.campaign.scheduledTz),
    refetchQuote: i.refetchQuote,
  });
  const controls = {
    view: purchase.view,
    depositPreference: state.depositPreference,
    onPickDepositMode: (mode: DepositPreference) => dispatch({ type: "pickDepositMode", mode }),
    onBuy: purchase.openBuy,
    onCheckAgain: purchase.checkAgain,
    disabled: state.busy || purchase.busy,
  };
  return { controls, buyModal: purchase.buyModal };
}

// ---- presentational pieces -----------------------------------------------------------------------

/** The details step's cost line (B3), the prototype's `.cx-ac-cost`. */
function CostLine({ line }: { line: { kind: "free" | "paid"; text: string } }) {
  const paid = line.kind === "paid";
  const Icon = paid ? AddCardOutlinedIcon : CheckCircleOutlineIcon;
  return (
    <p
      className={[
        "flex items-center gap-2 rounded-lg px-3.5 py-2.5 text-[13.5px]",
        paid ? "bg-light-dust text-rodeo-black" : "bg-good-sage-4 text-good-sage",
      ].join(" ")}
    >
      <Icon sx={{ fontSize: 18, flexShrink: 0 }} />
      {line.text}
    </p>
  );
}

function DetailsStep({
  idPrefix,
  envelope,
  campaign,
  draft,
  form,
  errors,
  disabled,
  onChange,
}: {
  idPrefix: string;
  envelope: CampaignContactsResponse;
  campaign: SendTimeCampaign;
  draft: boolean;
  form: RecipientFormValue;
  errors: RecipientFormErrors;
  disabled: boolean;
  onChange: (value: RecipientFormValue) => void;
}) {
  const zone = resolveScheduleTimeZone(campaign.scheduledTz);
  const costLine = detailsCostLine({
    draft,
    n: 1,
    spacesFree: envelope.spacesFree,
    pricePerGift: envelope.budget?.pricePerGift ?? 0,
  });
  return (
    <>
      <RecipientDetailsForm
        idPrefix={idPrefix}
        value={form}
        errors={errors}
        onChange={onChange}
        automation={campaign.automation}
        minSendDate={minSendDateInput(zone)}
        maxSendDate={maxSendDateInput(campaign.expireDate, zone)}
        disabled={disabled}
      />
      {costLine && <CostLine line={costLine} />}
    </>
  );
}

/** The send-time step wired to the wizard state; the recap and "Change" exist only after payment. */
function SendTimeStep({
  state,
  dispatch,
  campaign,
  quote,
  error,
}: {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
  campaign: SendTimeCampaign;
  quote: RecipientAdditionsQuote | undefined;
  error: string | null;
}) {
  const afterPayment = wizardSteps(state.plan).includes("payment");
  const batchDate = campaign.automation
    ? {
        value: state.batchSendDate,
        onChange: (date: string) => dispatch({ type: "setBatchSendDate", date }),
      }
    : undefined;
  return (
    <AddRecipientSendTimeStep
      campaign={campaign}
      choice={state.sendTime}
      onPick={(mode) => dispatch({ type: "pickSendMode", mode })}
      onDate={(date) => dispatch({ type: "setSendDate", date })}
      onTime={(time) => dispatch({ type: "setSendTime", time })}
      batchDate={batchDate}
      recap={afterPayment && quote ? paymentRecap(quote) : null}
      onChange={afterPayment ? () => dispatch({ type: "change" }) : undefined}
      error={error}
    />
  );
}

function WizardFooter({
  backLabel,
  onBack,
  primaryLabel,
  forward,
  busy,
  disabled,
}: {
  backLabel: string;
  onBack: () => void;
  primaryLabel: string;
  /** Another step follows: the button points onward. */
  forward: boolean;
  busy: boolean;
  disabled: boolean;
}) {
  return (
    <div className="mt-2 flex flex-wrap items-center justify-end gap-3">
      <GfgButton type="button" variant="secondary" onClick={onBack} disabled={busy}>
        {backLabel}
      </GfgButton>
      <GfgButton
        type="submit"
        variant="primary"
        disabled={disabled}
        endIcon={forward ? <ArrowForwardIcon sx={{ fontSize: 15 }} /> : undefined}
      >
        {primaryLabel}
      </GfgButton>
    </div>
  );
}

// ---- the wizard ----------------------------------------------------------------------------------

interface WizardBodyProps {
  titleId: string;
  campaignId: string;
  envelope: CampaignContactsResponse;
  entry: WizardEntry;
  rows: readonly RecipientAdditionRowBody[];
  importReview?: AddRecipientWizardProps["importReview"];
  submit: SubmitAddition;
  onClose: () => void;
  onAdded: AddRecipientWizardProps["onAdded"];
}

/** The wizard's state, its form, the quote it follows and its two handlers (submit, form edits). */
function useWizard(props: WizardBodyProps) {
  const { campaignId, envelope, entry, rows, onClose } = props;
  const campaign = useMemo(() => sendTimeCampaignOf(envelope), [envelope]);
  const n = entry === "bulk" ? rows.length : 1;
  const wizard = useWizardState({ campaignId, envelope, campaign, entry, rows, n });
  const { state, dispatch, quote } = wizard;
  const recheck = state.step === "details" && state.notice !== null;
  const recipient = useRecipientForm({ envelope, campaign, recheck });
  const confirm = useConfirm({
    submit: props.submit,
    dispatch,
    onFieldErrors: recipient.showFieldErrors,
    campaignName: envelope.campaign.name,
    onAdded: props.onAdded,
    onClose,
  });
  const credits = useCreditsPurchase({
    state,
    dispatch,
    quote,
    campaign,
    refetchQuote: wizard.quoteQuery.refetch,
  });
  const sendError = sendStepError(state, campaign, new Date());
  const importBlocked = (props.importReview?.errors.length ?? 0) > 0;
  const disabled = primaryDisabled({
    state,
    quote,
    fetching: wizard.quoteStale,
    sendError,
    importBlocked,
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (disabled || (state.step === "details" && !recipient.validate())) return;
    if (isLastStep(state)) {
      void confirm(additionBody({ entry, form: recipient.form, rows, campaign, state, quote }));
    } else {
      dispatch({ type: "next" });
    }
  }

  function handleFormChange(value: RecipientFormValue) {
    recipient.setForm(value);
    if (state.notice !== null) dispatch({ type: "dismissNotice" });
  }

  return {
    ...wizard,
    campaign,
    n,
    form: recipient.form,
    formErrors: recipient.errors,
    creditsPurchase: credits.controls,
    buyModal: credits.buyModal,
    sendError,
    disabled,
    handleSubmit,
    handleFormChange,
  };
}

type Wizard = ReturnType<typeof useWizard>;

/** The current step's body: the recipient form, the payment review or the send time. */
function StepBody({
  idPrefix,
  envelope,
  w,
}: {
  idPrefix: string;
  envelope: CampaignContactsResponse;
  w: Wizard;
}) {
  const { state } = w;
  switch (state.step) {
    case "details":
      return (
        <DetailsStep
          idPrefix={idPrefix}
          envelope={envelope}
          campaign={w.campaign}
          draft={state.plan.draft}
          form={w.form}
          errors={w.formErrors}
          disabled={state.busy}
          onChange={w.handleFormChange}
        />
      );
    case "payment":
      return (
        <AddRecipientPaymentStep
          quote={w.quote}
          loading={w.quoteStale}
          error={w.quoteQuery.isError ? quoteFailure(w.quoteQuery.error) : null}
          notice={state.notice}
          creditsPurchase={w.creditsPurchase}
        />
      );
    case "sendTime":
      return (
        <SendTimeStep
          state={state}
          dispatch={w.dispatch}
          campaign={w.campaign}
          quote={w.quote}
          error={w.sendError}
        />
      );
  }
}

/** One run of the wizard: mounted when the dialog opens, so every open starts afresh. */
function WizardBody(props: WizardBodyProps) {
  const { titleId, envelope, onClose, importReview } = props;
  const id = useId();
  const w = useWizard(props);
  const { state, dispatch } = w;
  const steps = wizardSteps(state.plan);
  const first = state.step === steps[0];
  const giftsBudgeted = envelope.budget?.giftsBudgeted ?? null;
  const heading = wizardTitle(state.step, state.plan, w.n, envelope.campaign.name, giftsBudgeted);
  const actions = COPY.addWizard.actions;

  return (
    <>
      <form onSubmit={w.handleSubmit} noValidate className="flex flex-col gap-4">
        <ContactsDialogHeader
          titleId={titleId}
          title={heading.title}
          subtitle={heading.subtitle}
          onClose={() => state.busy || onClose()}
          compact
        />
        <StepBody idPrefix={id} envelope={envelope} w={w} />
        {state.step !== "payment" && state.notice !== null && (
          <StepNotice>{state.notice}</StepNotice>
        )}
        {importReview !== undefined && <ImportReviewNotices review={importReview} />}
        <WizardFooter
          backLabel={first ? actions.cancel : actions.back}
          onBack={() => (first ? onClose() : dispatch({ type: "back" }))}
          primaryLabel={
            state.busy ? actions.processing : primaryActionLabel(state.step, state.plan, w.n)
          }
          forward={!isLastStep(state) && !state.busy}
          busy={state.busy}
          disabled={w.disabled}
        />
      </form>
      {w.buyModal !== null && (
        <ThemeProvider theme={aboveWizardDialog}>
          <BuyCreditsModal {...w.buyModal} />
        </ThemeProvider>
      )}
    </>
  );
}

/**
 * Add recipients (spec B3–B9): details → payment → send time, per the plan the quote implies. A
 * draft only takes details; a free addition skips payment; bulk starts at payment. Nothing is
 * written until the last step's ADD RECIPIENT(S) — the one call to
 * `POST /campaigns/:campaignId/contacts/additions` (B8). Credits the wallet lacks are bought before
 * that, in JR's Buy Credits modal (R4); the write only links that purchase.
 */
export default function AddRecipientWizard({
  open,
  onClose,
  campaignId,
  envelope,
  entry,
  initialRows,
  importReview,
  onAdded,
}: AddRecipientWizardProps) {
  const titleId = useId();
  const { addRecipients } = useCampaignContactActions(campaignId);

  return (
    <Dialog
      open={open}
      onClose={() => addRecipients.isPending || onClose()}
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: CONTACTS_FORM_PAPER_SX } }}
    >
      <WizardBody
        titleId={titleId}
        campaignId={campaignId}
        envelope={envelope}
        entry={entry}
        rows={initialRows ?? NO_ROWS}
        importReview={importReview}
        submit={addRecipients.mutateAsync}
        onClose={onClose}
        onAdded={onAdded}
      />
    </Dialog>
  );
}
